import { Dtos } from "@inrev/common";
import type { SfaaClassExperienceRowsQuery } from "@portal/api/sfaa-class-experience";
import {
	useFetchSfaaClassExperienceFilters,
	useFetchSfaaClassExperienceRows,
} from "@portal/api/sfaa-class-experience/admin";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors/ReportedRetryableLoadError";
import { Text } from "@portal/components/primitives";
import { ServerDynamicDataTable } from "@portal/components/ui/DynamicDataTable/DynamicDataTable";
import { readPersistedServerTableState } from "@portal/components/ui/DynamicDataTable/useServerDynamicDataTableState";
import { SearchInput } from "@portal/components/ui/SearchInput";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { sfaaClassExperienceColumns } from "./columns";

const TABLE_NAME = "sfaa-class-experience";
const DEFAULT_SORTING: SortingState = [{ id: "year", desc: true }];

const getFilterValues = (filters: ColumnFiltersState, id: string): string[] => {
	const value = filters.find((filter) => filter.id === id)?.value;
	if (!Array.isArray(value)) return [];
	return value.filter((item): item is string => typeof item === "string");
};

const getYears = (filters: ColumnFiltersState): number[] =>
	getFilterValues(filters, "year")
		.map(Number)
		.filter((year) => Number.isInteger(year));

export const SfaaClassExperienceExplorer = () => {
	// Seed before mount so restored filters do not trigger an initial unfiltered request.
	const [persisted] = useState(() => readPersistedServerTableState(TABLE_NAME, DEFAULT_SORTING));
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(persisted.columnFilters);
	const [sorting, setSorting] = useState<SortingState>(persisted.sorting);
	const [search, setSearch] = useState("");

	const query = useMemo((): SfaaClassExperienceRowsQuery => {
		const activeSort = sorting[0];
		const parsedSortField = Dtos.Admin.SfaaClassExperience.rowSortFieldSchema.safeParse(
			activeSort?.id,
		);

		return {
			lines: getFilterValues(columnFilters, "line"),
			sublines: getFilterValues(columnFilters, "subline"),
			locations: getFilterValues(columnFilters, "location"),
			sfaaCodes: getFilterValues(columnFilters, "sfaaCode"),
			years: getYears(columnFilters),
			minYear: undefined,
			maxYear: undefined,
			// Whitespace must not trigger an error state that hides the input needed to clear it.
			search: search.trim() || undefined,
			sortField: parsedSortField.success ? parsedSortField.data : "year",
			sortDirection: activeSort?.desc === false ? "asc" : "desc",
		};
	}, [columnFilters, search, sorting]);

	const rowsQuery = useFetchSfaaClassExperienceRows(query);
	const filtersQuery = useFetchSfaaClassExperienceFilters();

	const rows = useMemo(
		() => rowsQuery.data?.pages.flatMap((page) => page.rows) ?? [],
		[rowsQuery.data],
	);
	const totalCount = rowsQuery.data?.pages[0]?.totalCount ?? 0;
	const facetValues = useMemo(
		() =>
			filtersQuery.data
				? {
						line: filtersQuery.data.lines,
						subline: filtersQuery.data.sublines,
						location: filtersQuery.data.locations,
						sfaaCode: filtersQuery.data.sfaaCodes,
						year: filtersQuery.data.years.map(String),
					}
				: undefined,
		[filtersQuery.data],
	);

	const loadMore = useCallback(() => {
		if (rowsQuery.hasNextPage && !rowsQuery.isFetchingNextPage) {
			void rowsQuery.fetchNextPage();
		}
	}, [rowsQuery.fetchNextPage, rowsQuery.hasNextPage, rowsQuery.isFetchingNextPage]);

	const loadError = rowsQuery.error ?? filtersQuery.error;
	const retry = useCallback(() => {
		void Promise.all([rowsQuery.refetch(), filtersQuery.refetch()]);
	}, [filtersQuery.refetch, rowsQuery.refetch]);

	// Preserve loaded pages and scroll position when a later page request fails.
	const hasNothingToShow = rowsQuery.data === undefined;
	if (loadError && hasNothingToShow) {
		return (
			<ReportedRetryableLoadError
				error={loadError}
				resource="SFAA class experience data"
				onRetry={retry}
				retrying={rowsQuery.isFetching || filtersQuery.isFetching}
			/>
		);
	}

	return (
		<div className="flex h-full min-h-0 flex-col gap-3">
			<div className="flex shrink-0 items-center justify-between gap-4">
				<Text type="body" color="secondary">
					{totalCount.toLocaleString()} {totalCount === 1 ? "row" : "rows"}
				</Text>
				<SearchInput
					name="sfaa-class-experience-search"
					placeholder="Search class experience"
					onChange={setSearch}
					className="border border-bc-default"
					testId="sfaa-class-experience-search"
				/>
			</div>
			{loadError ? (
				<Text type="caption" color="danger">
					Some rows could not be loaded. Scroll up to keep working with what is already here, or
					retry to reload.
				</Text>
			) : null}
			<ServerDynamicDataTable
				name={TABLE_NAME}
				columns={sfaaClassExperienceColumns}
				rows={rows}
				totalCount={totalCount}
				facetValues={facetValues}
				initialSorting={DEFAULT_SORTING}
				onFiltersChange={setColumnFilters}
				onSortingChange={setSorting}
				onLoadMore={loadMore}
				isLoadingNextPage={rowsQuery.isFetchingNextPage}
				dataLoading={rowsQuery.isLoading || filtersQuery.isLoading}
			/>
		</div>
	);
};
